import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { checkExpiration, updateActivity, clearDataExpiredFlag } from '../store/slices/userSlice';
import { TIMEOUT_DURATION_MS, USER_DATA_EXPIRATION_MS } from './constants';

export const useRestartTimer = () => {
    const dispatch = useAppDispatch();
    
    return () => {
        // Reset the timeout state
        dispatch({ type: 'exhibit/setTimeout', payload: false });

        // Set up a new timeout using the configured duration
        const timeoutId = setTimeout(() => {
            dispatch({ type: 'exhibit/setTimeout', payload: true });
        }, TIMEOUT_DURATION_MS);

        // Clean up the timeout when component unmounts
        return () => {
            clearTimeout(timeoutId);
        };
    };
};

/**
 * Custom hook to handle automatic user data expiration
 * Checks for expiration on mount and sets up periodic checks
 * Navigates to home page when data expires but visitorId is preserved
 */
export const useUserDataExpiration = () => {
    const dispatch = useAppDispatch();
    const userState = useAppSelector(state => state.user);
    const navigate = useNavigate();
    const location = useLocation();
    
    useEffect(() => {
        // Check expiration immediately on mount
        dispatch(checkExpiration());
        
        // Set up periodic expiration checks every 5 minutes
        const expirationCheckInterval = setInterval(() => {
            dispatch(checkExpiration());
        }, 5 * 60 * 1000); // 5 minutes
        
        // Clean up interval on unmount
        return () => {
            clearInterval(expirationCheckInterval);
        };
    }, [dispatch]);
    
    // Handle navigation when data expires
    useEffect(() => {
        if (userState.dataWasExpired && userState.visitorId) {
            console.log('🔄 Data expired but visitorId preserved, redirecting to language selection...');
            
            // Clear the flag to prevent repeated redirects
            dispatch(clearDataExpiredFlag());
            
            // Navigate to language selection to restart the flow
            // Don't redirect if already on allowed pages
            const allowedPages = ['/language', '/privacy', '/register'];
            if (!allowedPages.includes(location.pathname)) {
                navigate('/language', { replace: true });
            }
        }
    }, [userState.dataWasExpired, userState.visitorId, navigate, location.pathname, dispatch]);
    
    // Function to manually update user activity
    const updateUserActivity = () => {
        dispatch(updateActivity());
    };
    
    return {
        updateUserActivity,
        isExpired: !userState.lastActivityAt || 
                  (Date.now() - userState.lastActivityAt) > USER_DATA_EXPIRATION_MS,
        dataWasExpired: userState.dataWasExpired
    };
};

/**
 * Custom hook to track user activity and update timestamps
 * Should be used in main app components to track user interactions
 */
export const useActivityTracker = () => {
    const dispatch = useAppDispatch();
    
    useEffect(() => {
        // Track various user activities
        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
        
        const handleActivity = () => {
            dispatch(updateActivity());
        };
        
        // Add event listeners for user activity
        activityEvents.forEach(event => {
            document.addEventListener(event, handleActivity, { passive: true });
        });
        
        // Clean up event listeners
        return () => {
            activityEvents.forEach(event => {
                document.removeEventListener(event, handleActivity);
            });
        };
    }, [dispatch]);
};

export const useGetExhibitName = () => {
    const { t } = useTranslation();

    return (exhibitId: string) => {
        return t(`navigation.${exhibitId}`);
    };
};
